import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import StarRating from "./StarRating";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return ""; }
};

export default function ProductReviews({ productId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/products/${productId}/reviews`).then(({ data }) => setData(data)).catch(() => setData({ avg: 0, count: 0, reviews: [], breakdown: {}, my_review: null, eligibility: { logged_in: false, eligible: false } }));

  useEffect(() => { if (productId) load(); }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (stars) => {
    setBusy(true);
    try {
      await api.post(`/products/${productId}/reviews`, { stars });
      toast.success(data?.my_review ? "Rating updated" : "Thanks for the rating");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save rating");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;

  const { avg, count, reviews, my_review, eligibility } = data;

  return (
    <div className="mt-8" data-testid="product-reviews-block">
      {/* Summary — avg + count, brutalist */}
      <div className="brut-border bg-white p-4 flex flex-wrap items-center gap-4" data-testid="review-summary">
        <div>
          <StarRating value={Math.round(avg)} readOnly size="md" testIdPrefix="avg-star" />
        </div>
        <div className="mono text-xs uppercase tracking-[0.25em] text-[#555]">
          <span data-testid="review-avg" className="text-[#C77E3C] font-bold text-base">{avg.toFixed(1)}</span>
          <span className="mx-2">/</span>
          <span>5</span>
          <span className="mx-3">·</span>
          <span data-testid="review-count">{count} {count === 1 ? "rating" : "ratings"}</span>
        </div>
      </div>

      {/* Rate widget */}
      {eligibility?.logged_in && eligibility?.eligible && (my_review == null || my_review) && (
        <div className="mt-4 brut-border bg-[#FDF3E9] p-4" data-testid="rate-widget">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">
            {my_review ? "Your rating" : "Leave a rating"}
          </div>
          <div className="mt-3 flex items-center gap-4">
            <StarRating
              value={my_review?.stars || 0}
              onChange={(s) => !busy && submit(s)}
              readOnly={busy || (my_review && eligibility.can_edit === false)}
              size="lg"
              testIdPrefix="my-star"
            />
            {my_review && (
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                Submitted {fmtDate(my_review.created_at)}
                {eligibility.can_edit ? " · editable for 30 days" : " · locked"}
              </div>
            )}
          </div>
        </div>
      )}
      {eligibility?.logged_in && !eligibility?.eligible && (
        <div className="mt-4 mono text-[10px] uppercase tracking-[0.25em] text-[#888]" data-testid="rate-unavailable">
          {eligibility.reason === "before_receipt"
            ? `Ratings open once you have received this item (available ${fmtDate(eligibility.receipt_date)}).`
            : eligibility.reason === "not_purchased"
            ? "Only buyers who purchased this product can leave a rating."
            : ""}
        </div>
      )}

      {/* Individual reviews list */}
      {reviews.length > 0 && (
        <div className="mt-6" data-testid="review-list">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#555] mb-3">
            {count} {count === 1 ? "review" : "reviews"}
          </div>
          <div className="brut-border bg-white">
            {reviews.map((r, i) => (
              <div key={r.review_id} data-testid={`review-item-${i}`}
                className={`p-4 flex items-center justify-between ${i > 0 ? "border-t-2 border-[#1F3D2A]" : ""}`}>
                <div className="flex items-center gap-3">
                  <StarRating value={r.stars} readOnly size="sm" testIdPrefix={`row-${i}-star`} />
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                    {r.buyer_name || (r.buyer_email || "").split("@")[0]}
                  </div>
                </div>
                <div className="mono text-[9px] uppercase tracking-[0.2em] text-[#888]">{fmtDate(r.updated_at || r.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
